import { ImageWithFallback } from "./figma/ImageWithFallback";
import { motion } from "framer-motion";

const steps = [
  {
    number: "01",
    title: "Create Your Anonymous Account",
    description: "Choose an anonymous username and confirm your email so you can participate in the community without sharing your personal details.",
  },
  {
    number: "02",
    title: "Browse & Contribute",
    description: "Explore the site and share product reviews in a quick 4-step flow: give feedback on product batches, upload photos, and add your insights. All reviews must follow community standards for accuracy and respectful sharing.",
  },
  {
    number: "03",
    title: "Unlock Full Access",
    description: "To access full product names and detailed info, verify your patient status at your clinic or with one of our ambassadors. Once verified, you'll unlock complete batch details and all platform features.",
  },
];

export function HowItWorks() {
  return (
    <section id="how-it-works" className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Steps */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl mb-6">
              Start your journey
              <span className="block text-primary">in three simple steps</span>
            </h2>
            
            <p className="text-xl text-muted-foreground mb-12">
              Add transparency to your cannabis experience with CannaBias.
            </p>

            <motion.div 
              className="space-y-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.15
                  }
                }
              }}
            >
              {steps.map((step, index) => (
                <motion.div 
                  key={index} 
                  className="flex gap-6 group"
                  variants={{
                    hidden: { opacity: 0, x: -30 },
                    visible: { opacity: 1, x: 0 }
                  }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-2xl gradient-green flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span className="text-2xl text-white">{step.number}</span>
                    </div>
                  </div>
                  <div className="pt-2">
                    <h3 className="text-xl mb-2">{step.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right: Image */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1706759755767-526cdaa02109?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwaG9uZSUyMHFyJTIwY29kZSUyMHNjYW58ZW58MXx8fHwxNzYwMzY4NDQwfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                alt="QR code scanning for patient verification"
                className="w-full h-[700px] object-cover"
              />
            </div>
            
            {/* Decorative Elements */}
            <motion.div 
              className="absolute -top-6 -right-6 w-32 h-32 bg-primary/10 rounded-full blur-3xl"
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div 
              className="absolute -bottom-6 -left-6 w-40 h-40 bg-accent/50 rounded-full blur-3xl"
              animate={{ 
                scale: [1, 1.3, 1],
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{ 
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.div>
        </div>

        {/* Why Verification Matters Section */}
        <motion.div 
          className="mt-24 pt-12 border-t border-border"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="mb-4">
              Why Verification Matters
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Verified patients get access to the entire product catalogue, can compare options, and view batch-level details. Non-verified users can still view clinic info and take part in community activities, but product names are restricted to meet UK regulations.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}